/* ============================================
   Comfort Club (sections/cc-studio, cc-year, cc-cta).

   Studio: shelf cards add their pick to the box and turn into a stepper;
   the box tray fills one compartment per savings step, card prices drop
   as the box grows, the receipt compares one-time prices, and checkout
   sends each line on its Recharge selling plan. On phones the box is a
   sheet opened from a bottom bar.

   The studio publishes its state as a document event (cc:box). The year
   planner and the closing band render from it; an empty box publishes
   the favorites box as an example so they are never blank.
   ============================================ */

(function () {
  if (window.ccClub) return; // every Club section loads this file
  window.ccClub = { state: null };

  var MONTH_DAYS = 30.44;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
  }

  function escapeHtml(text) {
    return String(text == null ? '' : text).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  /* Months between deliveries read from a plan's name and options. */
  function planMonths(label) {
    var match = /(\d+(?:\.\d+)?)\s*(day|week|month)/i.exec(label || '');
    if (!match) return null;
    var n = parseFloat(match[1]);
    var unit = match[2].toLowerCase();
    if (unit === 'day') return n / MONTH_DAYS;
    if (unit === 'week') return (n * 7) / MONTH_DAYS;
    return n;
  }

  function addMonths(date, n) {
    var d = new Date(date.getTime());
    var day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + n);
    var last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, last));
    return d;
  }

  function shortDate(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function publish(state) {
    window.ccClub.state = state;
    document.dispatchEvent(new CustomEvent('cc:box', { detail: state }));
  }

  /* ---------------------------------------------------------------- */
  /* Studio                                                            */
  /* ---------------------------------------------------------------- */

  function initStudio(root) {
    if (root.dataset.ccReady === '1') return;
    root.dataset.ccReady = '1';

    var format = root.getAttribute('data-money-format');
    var unitOne = root.getAttribute('data-unit-one') || 'item';
    var unitMany = root.getAttribute('data-unit-many') || 'items';
    var refPrice = parseInt(root.getAttribute('data-ref-price'), 10) || 0;
    var gift = {
      name: root.getAttribute('data-gift-name') || '',
      price: parseInt(root.getAttribute('data-gift-price'), 10) || 0,
      thumb: root.getAttribute('data-gift-thumb') || ''
    };
    var tiers = (root.getAttribute('data-tiers') || '')
      .split(',')
      .map(function (pair) {
        var parts = pair.split(':');
        return { qty: parseInt(parts[0], 10), pct: parseFloat(parts[1]) };
      })
      .filter(function (tier) {
        return tier.qty > 0 && !isNaN(tier.pct);
      });
    var firstPct = tiers[0] ? tiers[0].pct : 0;

    var box = root.querySelector('[data-cc-box]');
    var itemsEl = root.querySelector('[data-cc-items]');
    var countEl = root.querySelector('[data-cc-count]');
    var nudgeEl = root.querySelector('[data-cc-nudge]');
    var slots = Array.prototype.slice.call(root.querySelectorAll('[data-cc-slot]'));
    var datesEl = root.querySelector('[data-cc-dates]');
    var receipt = root.querySelector('[data-cc-receipt]');
    var fullEl = root.querySelector('[data-cc-full]');
    var totalEl = root.querySelector('[data-cc-total]');
    var yearlyEl = root.querySelector('[data-cc-yearly]');
    var cta = root.querySelector('[data-cc-cta]');
    var ctaText = cta ? cta.textContent.trim() : '';
    var status = root.querySelector('[data-cc-status]');
    var live = root.querySelector('[data-cc-live]');
    var fillButton = root.querySelector('[data-cc-fill]');
    var bar = root.querySelector('[data-cc-bar]');
    var barDots = Array.prototype.slice.call(root.querySelectorAll('[data-cc-bar-dot]'));
    var barCount = root.querySelector('[data-cc-bar-count]');
    var barLine = root.querySelector('[data-cc-bar-line]');
    var barTotal = root.querySelector('[data-cc-bar-total]');
    var openButton = root.querySelector('[data-cc-open]');
    var closeButton = root.querySelector('[data-cc-close]');
    var backdrop = root.querySelector('[data-cc-backdrop]');
    var storageKey = 'hc:club-box:' + window.location.pathname;

    var options = {}; // key -> option data
    var order = []; // keys in shelf order
    var lines = []; // [{key, qty}] in the order they were added
    var cards = [];
    var lastCount = 0;

    function plural(n) {
      return n + ' ' + (n === 1 ? unitOne : unitMany);
    }

    var scentMode = root.classList.contains('cc-studio--scents');

    function optionName(opt) {
      return opt.label ? opt.product + ', ' + opt.label : opt.product;
    }

    function track(name, params) {
      if (!window.HnTelemetry || typeof window.HnTelemetry.recordEvent !== 'function') return;
      try {
        window.HnTelemetry.recordEvent(name, params);
      } catch (e) {}
    }

    function lineFor(key) {
      for (var i = 0; i < lines.length; i++) if (lines[i].key === key) return lines[i];
      return null;
    }

    function qtyOf(key) {
      var line = lineFor(key);
      return line ? line.qty : 0;
    }

    function count(list) {
      return list.reduce(function (sum, line) {
        return sum + line.qty;
      }, 0);
    }

    function tierFor(items) {
      var reached = null;
      tiers.forEach(function (tier, index) {
        if (items >= tier.qty) reached = { pct: tier.pct, qty: tier.qty, index: index };
      });
      return reached;
    }

    function nextTier(items) {
      for (var i = 0; i < tiers.length; i++) if (tiers[i].qty > items) return tiers[i];
      return null;
    }

    function frequency() {
      var checked = root.querySelector('[data-cc-frequency]:checked');
      return checked ? parseInt(checked.value, 10) : parseInt(root.getAttribute('data-default-frequency'), 10) || 1;
    }

    /* Prices for a list of lines on a schedule. */
    function summarize(list, months) {
      var items = count(list);
      var tier = tierFor(items);
      var pct = tier ? tier.pct : 0;
      var full = 0;
      list.forEach(function (line) {
        full += options[line.key].price * line.qty;
      });
      var total = Math.round(full * (1 - pct / 100));
      var boxesPerYear = Math.max(1, Math.floor(12 / months));
      return {
        items: items,
        pct: pct,
        full: full,
        total: total,
        saving: full - total,
        months: months,
        boxesPerYear: boxesPerYear,
        yearSaving: (full - total) * boxesPerYear
      };
    }

    function starterKeys() {
      var keys = (root.getAttribute('data-starter') || '')
        .split(',')
        .map(function (key) {
          return key.trim();
        })
        .filter(function (key) {
          return options[key];
        });
      return keys.length ? keys : order.slice(0, 3);
    }

    /* ---- Shelf cards ---- */
    Array.prototype.forEach.call(root.querySelectorAll('[data-cc-item]'), function (card) {
      var list = [];
      try {
        list = JSON.parse(card.querySelector('[data-cc-options]').textContent) || [];
      } catch (e) {
        list = [];
      }
      list.forEach(function (opt) {
        options[opt.key] = opt;
        order.push(opt.key);
      });

      var entry = {
        el: card,
        keys: list.map(function (opt) {
          return opt.key;
        }),
        add: card.querySelector('[data-cc-add]'),
        qty: card.querySelector('[data-cc-qty]'),
        qtyValue: card.querySelector('[data-cc-qty-value]'),
        dec: card.querySelector('[data-cc-qty-dec]'),
        inc: card.querySelector('[data-cc-qty-inc]'),
        full: card.querySelector('[data-cc-card-full]'),
        price: card.querySelector('[data-cc-card-price]'),
        badge: card.querySelector('[data-cc-badge]'),
        picked: card.querySelector('[data-cc-picked]'),
        photo: card.querySelector('.cc-item__img')
      };
      entry.current = function () {
        var chosen = card.querySelector('[data-cc-swatch]:checked');
        return chosen ? chosen.value : entry.keys[0];
      };
      cards.push(entry);

      entry.show = function (input) {
        if (entry.picked) entry.picked.textContent = input.getAttribute('data-label') || '';
        // The card shows the picked scent's own bottle when it has one.
        var opt = options[input.value];
        if (entry.photo && opt && opt.photo) {
          entry.photo.removeAttribute('srcset');
          entry.photo.src = opt.photo;
        }
      };

      card.addEventListener('change', function (event) {
        if (!event.target.matches('[data-cc-swatch]')) return;
        entry.show(event.target);
        renderCards(currentPct());
      });

      entry.add.addEventListener('click', function () {
        var key = entry.current();
        if (!key) return;
        change(key, 1);
        if (entry.inc) entry.inc.focus({ preventScroll: true });
      });
      if (entry.inc) {
        entry.inc.addEventListener('click', function () {
          change(entry.current(), 1);
        });
      }
      if (entry.dec) {
        entry.dec.addEventListener('click', function () {
          var key = entry.current();
          change(key, -1);
          if (!qtyOf(key)) entry.add.focus({ preventScroll: true });
        });
      }
    });

    function currentPct() {
      var tier = tierFor(count(lines));
      return tier ? tier.pct : 0;
    }

    function renderCards(pct) {
      var shown = Math.max(pct, firstPct);
      cards.forEach(function (entry) {
        var key = entry.current();
        var opt = options[key];
        if (!opt) return;
        var q = qtyOf(key);
        var name = optionName(opt);
        entry.add.hidden = q > 0;
        entry.add.setAttribute('aria-label', 'Add ' + name + ' to your box');
        if (entry.qty) {
          entry.qty.hidden = q === 0;
          entry.qty.setAttribute('aria-label', name + ' in your box');
          entry.qtyValue.textContent = String(q);
          entry.dec.setAttribute('aria-label', 'Remove one ' + name);
          entry.inc.setAttribute('aria-label', 'Add one more ' + name);
        }
        if (entry.full) entry.full.textContent = formatMoney(opt.price, format);
        if (entry.price) entry.price.textContent = formatMoney(Math.round(opt.price * (1 - shown / 100)), format);

        var inBox = entry.keys.reduce(function (sum, k) {
          return sum + qtyOf(k);
        }, 0);
        entry.el.classList.toggle('is-in-box', inBox > 0);
        if (entry.badge) {
          entry.badge.hidden = inBox === 0;
          entry.badge.textContent = inBox + ' in box';
        }
      });
    }

    /* A scent added from outside its card (the favorites) becomes the card's pick. */
    function pickOnCard(key) {
      cards.forEach(function (entry) {
        if (entry.keys.indexOf(key) === -1 || entry.current() === key) return;
        var input = Array.prototype.filter.call(entry.el.querySelectorAll('[data-cc-swatch]'), function (el) {
          return el.value === key;
        })[0];
        if (!input) return;
        input.checked = true;
        entry.show(input);
      });
    }

    /* ---- Box ---- */
    function change(key, delta) {
      if (delta > 0) pickOnCard(key);
      var line = lineFor(key);
      if (!line) {
        if (delta <= 0) return;
        line = { key: key, qty: 0 };
        lines.push(line);
      }
      line.qty = Math.max(0, line.qty + delta);
      lines = lines.filter(function (l) {
        return l.qty > 0;
      });
      render();
      save();

      var opt = options[key];
      var items = count(lines);
      if (live && opt) {
        live.textContent =
          (delta > 0 ? 'Added ' : 'Removed ') + optionName(opt) + '. ' + (items ? plural(items) + ' in your box. ' + nudgeText(items) : 'Your box is empty.');
      }
      if (delta > 0) bump();
      track('hi_club_box_change', { key: key, label: opt ? optionName(opt) : key, delta: delta, items: items, months: frequency() });
    }

    /* The box survives a reload or a trip to another page (this browser only). */
    function save() {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify({ lines: lines, months: frequency() }));
      } catch (e) {}
    }

    function restore() {
      try {
        var saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
        if (!saved || !Array.isArray(saved.lines)) return;
        lines = saved.lines.filter(function (line) {
          return options[line.key] && line.qty > 0;
        });
        if (saved.months) {
          var radio = root.querySelector('[data-cc-frequency][value="' + saved.months + '"]');
          if (radio) radio.checked = true;
        }
      } catch (e) {}
    }

    function unitPrice(pct) {
      return formatMoney(Math.round(refPrice * (1 - pct / 100)), format);
    }

    function nudgeText(items) {
      var next = nextTier(items);
      var tier = tierFor(items);
      if (!items) {
        return refPrice
          ? 'Add your first ' + unitOne + ' and it drops to ' + unitPrice(firstPct) + '.'
          : 'Add your first ' + unitOne + ' to save ' + firstPct + '%.';
      }
      if (next) {
        var need = next.qty - items;
        return refPrice
          ? 'Add ' + need + ' more and every ' + unitOne + ' drops to ' + unitPrice(next.pct) + '.'
          : 'Add ' + plural(need) + ' to save ' + next.pct + '% on everything.';
      }
      return refPrice
        ? 'Top saving unlocked: ' + unitPrice(tier.pct) + ' a ' + unitOne + '.'
        : 'Top saving unlocked: ' + tier.pct + '% off everything.';
    }

    function expanded() {
      var out = [];
      lines.forEach(function (line) {
        for (var i = 0; i < line.qty; i++) out.push(options[line.key]);
      });
      return out;
    }

    function renderTray(items) {
      var filled = expanded();
      slots.forEach(function (slot, index) {
        var tier = tiers[index];
        if (!tier) return;
        var isFilled = items >= tier.qty;
        var isLast = index === tiers.length - 1;
        var fill = slot.querySelector('[data-cc-slot-fill]');
        var opt = isFilled ? filled[tier.qty - 1] : null;
        var extra = isLast && items > tier.qty ? items - tier.qty : 0;
        var html = '';
        if (opt && opt.thumb) html += '<img src="' + escapeHtml(opt.thumb) + '" alt="" width="80" height="80">';
        if (extra) html += '<span class="cc-slot__more">+' + extra + '</span>';
        if (fill.innerHTML !== html) fill.innerHTML = html;
        var wasFilled = slot.classList.contains('is-filled');
        slot.classList.toggle('is-filled', isFilled);
        slot.classList.toggle('is-next', !isFilled && (index === 0 || items >= tiers[index - 1].qty));
        if (isFilled && !wasFilled && items > lastCount && !reduceMotion) {
          slot.classList.remove('is-new');
          void slot.offsetWidth; // restart the pop
          slot.classList.add('is-new');
        }
      });
    }

    function renderLines(pct) {
      itemsEl.innerHTML = lines
        .map(function (line) {
          var opt = options[line.key];
          var lineTotal = Math.round(opt.price * line.qty * (1 - pct / 100));
          /* One product in scents (Powder Club): the scent leads. Mixed
             products (Comfort Club): the product leads, its scent under. */
          var title = opt.label && scentMode ? opt.label : opt.product;
          var sub = opt.label
            ? (scentMode ? opt.product : opt.label) + (opt.size ? ', ' + opt.size : '')
            : '';
          var key = escapeHtml(line.key);
          return (
            '<li class="cc-line">' +
            (opt.thumb ? '<img class="cc-line__thumb" src="' + escapeHtml(opt.thumb) + '" alt="" width="40" height="40">' : '<span class="cc-line__thumb"></span>') +
            '<span class="cc-line__text"><span class="cc-line__name">' + escapeHtml(title) + '</span>' +
            (sub ? '<span class="cc-line__sub">' + escapeHtml(sub) + '</span>' : '') +
            '</span>' +
            '<span class="cc-line__stepper" role="group" aria-label="' + escapeHtml(optionName(opt)) + ' quantity">' +
            '<button type="button" data-cc-dec="' + key + '" aria-label="Remove one ' + escapeHtml(optionName(opt)) + '">&minus;</button>' +
            '<span class="cc-line__qty">' + line.qty + '</span>' +
            '<button type="button" data-cc-inc="' + key + '" aria-label="Add one more ' + escapeHtml(optionName(opt)) + '">+</button>' +
            '</span>' +
            '<span class="cc-line__price">' + formatMoney(lineTotal, format) + '</span>' +
            '</li>'
          );
        })
        .join('');
    }

    function renderDates(months) {
      if (!datesEl) return;
      var today = new Date();
      datesEl.textContent = 'First box now, then around ' + shortDate(addMonths(today, months)) + ' and ' + shortDate(addMonths(today, months * 2)) + '.';
    }

    function render() {
      var months = frequency();
      var sum = summarize(lines, months);
      var items = sum.items;
      var next = nextTier(items);

      renderTray(items);
      renderLines(sum.pct);
      renderCards(sum.pct);
      renderDates(months);

      countEl.textContent = items ? plural(items) : '';
      nudgeEl.textContent = nudgeText(items);

      receipt.hidden = !items;
      fullEl.textContent = sum.saving ? formatMoney(sum.full, format) : '';
      totalEl.textContent = formatMoney(sum.total, format);
      /* Automatic renewal disclosure beside the button. */
      var renewsEl = root.querySelector('[data-cc-renews]');
      if (renewsEl) {
        renewsEl.hidden = !items;
        renewsEl.textContent = items
          ? 'Renews ' + (months === 1 ? 'every month' : 'every ' + months + ' months') + ' at ' +
            formatMoney(sum.total, format) + ' until you cancel.'
          : '';
      }
      if (yearlyEl && items) {
        yearlyEl.innerHTML =
          'You save ' + formatMoney(sum.saving, format) + ' a box, <strong>' + formatMoney(sum.yearSaving, format) + ' a year</strong>.';
      }

      cta.disabled = items === 0;
      cta.textContent = items ? ctaText + ' · ' + formatMoney(sum.total, format) : ctaText;
      if (fillButton) fillButton.hidden = items > 0;

      barDots.forEach(function (dot, index) {
        dot.classList.toggle('is-filled', Boolean(tiers[index]) && items >= tiers[index].qty);
      });
      if (barCount) barCount.textContent = items ? '(' + items + ')' : '';
      if (barLine) {
        if (!items) barLine.textContent = 'Add a ' + unitOne + ' to save ' + firstPct + '%';
        else if (next) barLine.textContent = sum.pct + '% off · ' + (next.qty - items) + ' more for ' + next.pct + '%';
        else barLine.textContent = sum.pct + '% off everything';
      }
      if (barTotal) barTotal.textContent = items ? formatMoney(sum.total, format) : '';
      updateBar();
      setStatus('');

      // Share the box with the year planner and the closing band; an empty
      // box shows the favorites as the example.
      var example = !items;
      var shareLines = example
        ? starterKeys().map(function (key) {
            return { key: key, qty: 1 };
          })
        : lines;
      var shared = example ? summarize(shareLines, months) : sum;
      publish({
        example: example,
        items: shared.items,
        pct: shared.pct,
        full: shared.full,
        total: shared.total,
        saving: shared.saving,
        months: months,
        boxesPerYear: shared.boxesPerYear,
        yearSaving: shared.yearSaving,
        lines: shareLines.map(function (line) {
          var opt = options[line.key];
          return { thumb: opt.thumb, name: optionName(opt), qty: line.qty };
        }),
        unitOne: unitOne,
        unitMany: unitMany,
        format: format,
        gift: gift
      });
      lastCount = items;
    }

    function setStatus(text, isError) {
      if (!status) return;
      status.textContent = text || '';
      status.hidden = !text;
      status.classList.toggle('is-error', Boolean(isError));
    }

    itemsEl.addEventListener('click', function (event) {
      var inc = event.target.closest('[data-cc-inc]');
      var dec = event.target.closest('[data-cc-dec]');
      if (inc) change(inc.getAttribute('data-cc-inc'), 1);
      else if (dec) change(dec.getAttribute('data-cc-dec'), -1);
    });

    var lastFrequency = frequency();
    root.addEventListener('change', function (event) {
      if (!event.target.matches('[data-cc-frequency]')) return;
      var months = frequency();
      if (months !== lastFrequency) {
        track('hi_club_schedule_change', { surface: 'club_page', months: months, from_months: lastFrequency });
        lastFrequency = months;
      }
      render();
      save();
    });

    if (fillButton) {
      fillButton.addEventListener('click', function () {
        var keys = starterKeys();
        keys.forEach(function (key, index) {
          window.setTimeout(function () {
            change(key, 1);
          }, reduceMotion ? 0 : index * 140);
        });
        track('hi_club_box_change', { key: 'favorites', label: 'Favorites box', delta: keys.length, items: keys.length, months: frequency() });
        var first = cards[0] && cards[0].inc;
        if (first) {
          window.setTimeout(function () {
            first.focus({ preventScroll: true });
          }, reduceMotion ? 0 : keys.length * 140);
        }
      });
    }

    /* ---- Phone bar and sheet ---- */
    var studioInView = false;

    function updateBar() {
      if (!bar) return;
      bar.classList.toggle('is-visible', studioInView || count(lines) > 0);
    }

    function bump() {
      if (!bar || reduceMotion) return;
      bar.classList.remove('is-bump');
      void bar.offsetWidth;
      bar.classList.add('is-bump');
    }

    if ('IntersectionObserver' in window && bar) {
      new IntersectionObserver(
        function (entries) {
          studioInView = entries[0].isIntersecting;
          updateBar();
        },
        { rootMargin: '0px 0px -30% 0px' }
      ).observe(root);
    }

    /* Open, the sheet is a modal dialog: focus moves in, Tab stays inside,
       Escape or the backdrop closes it and focus returns to the bar. */
    function openSheet() {
      if (window.matchMedia('(min-width: 990px)').matches) return;
      root.classList.add('is-box-open');
      if (backdrop) backdrop.hidden = false;
      if (openButton) openButton.setAttribute('aria-expanded', 'true');
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      document.documentElement.style.overflow = 'hidden';
      if (closeButton) closeButton.focus();
    }

    function closeSheet() {
      if (!root.classList.contains('is-box-open')) return;
      root.classList.remove('is-box-open');
      if (backdrop) backdrop.hidden = true;
      box.removeAttribute('role');
      box.removeAttribute('aria-modal');
      if (openButton) {
        openButton.setAttribute('aria-expanded', 'false');
        openButton.focus();
      }
      document.documentElement.style.overflow = '';
    }

    function trapFocus(event) {
      if (event.key !== 'Tab' || !root.classList.contains('is-box-open')) return;
      var focusable = Array.prototype.filter.call(box.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])'), function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    if (openButton) openButton.addEventListener('click', openSheet);
    if (closeButton) closeButton.addEventListener('click', closeSheet);
    if (backdrop) backdrop.addEventListener('click', closeSheet);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeSheet();
      trapFocus(event);
    });
    /* Widening past the breakpoint (a tablet turned sideways) leaves no sheet to close. */
    window.matchMedia('(min-width: 990px)').addEventListener('change', function (event) {
      if (event.matches) closeSheet();
    });
    /* The closing band's button opens the box on phones. */
    document.addEventListener('cc:open-box', openSheet);

    /* ---- Checkout ---- */
    function planFor(opt, months, pct) {
      var best = null;
      (opt.plans || []).forEach(function (plan) {
        var planMonth = planMonths(plan.label);
        if (planMonth === null || Math.abs(planMonth - months) > 0.25) return;
        if (plan.pct > pct + 0.001) return;
        if (!best || plan.pct > best.pct) best = plan;
      });
      return best;
    }

    cta.addEventListener('click', function () {
      var pct = currentPct();
      var months = frequency();
      var cartLines = [];
      var missing = [];
      lines.forEach(function (line) {
        var opt = options[line.key];
        var plan = planFor(opt, months, pct);
        if (!plan) {
          missing.push(optionName(opt));
          return;
        }
        /* No line properties: Recharge copies them onto every renewal.
           Where the box started goes on the order (markSource below). */
        cartLines.push({ id: opt.id, quantity: line.qty, selling_plan: plan.id });
      });

      if (missing.length) {
        console.info('[cc-studio] no Recharge plan yet for:', missing.join('; '));
        setStatus('The Comfort Club opens soon. Subscriptions are not switched on yet.', false);
        return;
      }

      track('hi_checkout_click', { source: 'comfort_club', items: count(lines), months: months, saving_pct: pct });
      cta.disabled = true;
      cta.setAttribute('aria-busy', 'true');
      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: cartLines })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || data.message || 'Could not start the box');
            return data;
          });
        })
        .then(function () {
          // Let the cart rules (assets/cc-cart.js) settle first: the whole
          // cart's subscription lines on one saving, and the free puff added.
          if (window.ccCart && typeof window.ccCart.reconcile === 'function') return window.ccCart.reconcile();
          return null;
        })
        .then(function () {
          if (window.ccCart && typeof window.ccCart.markSource === 'function') return window.ccCart.markSource('club:' + window.location.pathname);
          return null;
        })
        .then(function () {
          window.location.href = '/checkout';
        })
        .catch(function (error) {
          console.error('[cc-studio] checkout failed:', error);
          setStatus('That did not go through. Please try again.', true);
          cta.disabled = false;
          cta.removeAttribute('aria-busy');
        });
    });

    restore();
    lastCount = count(lines);
    render();
  }

  /* ---------------------------------------------------------------- */
  /* Year planner (sections/cc-year)                                   */
  /* ---------------------------------------------------------------- */

  function thumbsHtml(lines, max) {
    var html = '';
    var shown = 0;
    lines.forEach(function (line) {
      if (shown >= max || !line.thumb) return;
      html += '<img src="' + escapeHtml(line.thumb) + '" alt="" width="40" height="40" loading="lazy">';
      shown++;
    });
    return html;
  }

  function initYear(el) {
    if (el.dataset.ccReady === '1') return;
    el.dataset.ccReady = '1';
    var note = el.querySelector('[data-cc-year-note]');
    var keep = el.querySelector('[data-cc-keep]');
    var keepLabel = el.querySelector('[data-cc-keep-label]');
    var compare = el.querySelector('[data-cc-compare]');
    var chipDate = el.querySelector('[data-cc-chip-date]');
    var chipSave = el.querySelector('[data-cc-chip-save]');

    var CHECK = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="currentColor"/><path d="M6 10.3l2.6 2.6L14 7.5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var CROSS = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="1.4"/><path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

    function row(label, club, self, clubGood, selfGood) {
      return (
        '<div class="cc-compare__row" role="row">' +
        '<span class="cc-compare__label" role="rowheader">' + label + '</span>' +
        '<span class="cc-compare__club" role="cell">' + (clubGood ? CHECK : '') + '<span>' + club + '</span></span>' +
        '<span class="cc-compare__self" role="cell">' + (selfGood === false ? CROSS : '') + '<span>' + self + '</span></span>' +
        '</div>'
      );
    }

    function render(state) {
      if (!state) return;
      var f = function (cents) {
        return formatMoney(cents, state.format);
      };
      var units = state.items + ' ' + (state.items === 1 ? state.unitOne : state.unitMany);
      var every = state.months === 1 ? 'every month' : 'every ' + state.months + ' months';
      var gift = state.gift && state.gift.name ? state.gift : null;
      var boxes = state.boxesPerYear;

      if (note) {
        note.textContent = state.example
          ? 'Shown for our favorites, ' + units + ' ' + every + '. Fill your box and this becomes yours.'
          : 'For your box: ' + units + ' ' + every + '.';
      }

      if (keep) {
        // Dollars large, cents set small beside them.
        var money = f(state.yearSaving);
        var dot = money.lastIndexOf('.');
        keep.innerHTML = dot > -1 ? escapeHtml(money.slice(0, dot)) + '<span>' + escapeHtml(money.slice(dot)) + '</span>' : escapeHtml(money);
      }
      if (keepLabel) {
        keepLabel.textContent = 'back to you every year' + (gift ? ', plus a ' + f(gift.price) + ' ' + gift.name + ' in your first box.' : '.');
      }

      if (chipDate) chipDate.textContent = 'Ships ' + shortDate(addMonths(new Date(), state.months)) + ', free';
      if (chipSave) chipSave.textContent = f(state.saving) + ' (' + state.pct + '% off)';

      if (compare) {
        compare.innerHTML =
          '<div class="cc-compare__row cc-compare__row--head" role="row">' +
          '<span role="columnheader"><span class="visually-hidden">What you get</span></span>' +
          '<span class="cc-compare__club" role="columnheader">Comfort Club</span>' +
          '<span class="cc-compare__self" role="columnheader">Reordering yourself</span>' +
          '</div>' +
          row('Each box of ' + escapeHtml(units), f(state.total), f(state.full), true, null) +
          row('Shipping', 'Free, every box', 'Free over $35', true, null) +
          (gift ? row(escapeHtml(gift.name), 'Free in box one', f(gift.price), true, false) : '') +
          row('Restocking', 'Arrives on schedule', 'Remember to reorder', true, false) +
          '<div class="cc-compare__row cc-compare__row--total" role="row">' +
          '<span class="cc-compare__label" role="rowheader">A year (' + boxes + ' boxes)</span>' +
          '<span class="cc-compare__club" role="cell">' + f(state.total * boxes) + '</span>' +
          '<span class="cc-compare__self" role="cell"><s>' + f(state.full * boxes) + '</s></span>' +
          '</div>';
      }
    }

    document.addEventListener('cc:box', function (event) {
      render(event.detail);
    });
    render(window.ccClub.state);
  }

  /* ---------------------------------------------------------------- */
  /* Closing band (sections/cc-cta)                                    */
  /* ---------------------------------------------------------------- */

  function initRecap(el) {
    if (el.dataset.ccReady === '1') return;
    el.dataset.ccReady = '1';
    var text = el.querySelector('[data-cc-recap-text]');
    var button = el.querySelector('[data-cc-recap-button]');
    var thumbs = el.querySelector('[data-cc-recap-thumbs]');
    var idleText = text ? text.textContent : '';
    var idleLabel = button ? button.textContent : '';
    var hasBox = false;

    function render(state) {
      if (!state) return;
      hasBox = !state.example;
      var every = state.months === 1 ? 'every month' : 'every ' + state.months + ' months';
      if (text) {
        text.textContent = hasBox
          ? 'Your box: ' + state.items + ' ' + (state.items === 1 ? state.unitOne : state.unitMany) + ', ' + every + ', ' + formatMoney(state.total, state.format) + ' a box.'
          : idleText;
      }
      if (button) button.textContent = hasBox ? 'Finish my box' : idleLabel;
      if (thumbs) thumbs.innerHTML = hasBox ? thumbsHtml(state.lines, 4) : '';
    }

    if (button) {
      button.addEventListener('click', function (event) {
        if (!hasBox || window.matchMedia('(min-width: 990px)').matches) return;
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('cc:open-box'));
      });
    }

    document.addEventListener('cc:box', function (event) {
      render(event.detail);
    });
    render(window.ccClub.state);
  }

  function initAll() {
    document.querySelectorAll('[data-cc-studio]').forEach(initStudio);
    document.querySelectorAll('[data-cc-year]').forEach(initYear);
    document.querySelectorAll('[data-cc-recap]').forEach(initRecap);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
